import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Copy, Users, DollarSign, Share2 } from "lucide-react";
import { Button } from "../ui/button";
import { API } from "../../App";

export const ReferralCard = ({ token }) => {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${API}/referrals/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setInfo(res.data);
      } catch (e) {
        console.warn("Failed to load referral info:", e?.response?.status || e?.message);
      }
    };
    if (token) fetchInfo();
  }, [token]);

  if (!info) return null;

  const shareUrl = `${window.location.origin}/auth?mode=register&ref=${info.referral_code}`;

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(info.referral_code);
      toast.success("Referral code copied!");
    } catch {
      toast.error("Copy failed");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied!");
    } catch {
      toast.error("Copy failed");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      copyLink();
      return;
    }
    try {
      await navigator.share({
        title: "ExpatMatch — your personalized relocation guide",
        text: "I'm using ExpatMatch to figure out where to move next. Sign up with my code and we both get $10 off your first guide.",
        url: shareUrl,
      });
    } catch (e) {
      // User cancelled or share API rejected — only log non-abort errors
      if (e?.name !== "AbortError") {
        console.warn("Native share failed:", e?.message);
      }
    }
  };

  return (
    <div
      className="bg-gradient-to-br from-secondary/10 via-primary/5 to-accent/10 rounded-2xl border border-secondary/20 p-6 md:p-8 mb-12"
      data-testid="referral-card"
    >
      <div className="flex items-start gap-4 mb-5">
        <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center shrink-0">
          <Gift className="h-6 w-6 text-secondary" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-primary mb-1">
            Refer a friend — you both get $10
          </h3>
          <p className="text-sm text-muted-foreground">
            Share your code. When your friend buys their first guide, you each get
            $10 off your next purchase — automatically.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-5">
        <div className="bg-card rounded-xl p-4 flex items-center gap-3">
          <DollarSign className="h-5 w-5 text-accent shrink-0" />
          <div>
            <p
              className="font-mono text-xl font-bold text-primary"
              data-testid="referral-wallet-balance"
            >
              ${info.wallet_credit_dollars.toFixed(0)}
            </p>
            <p className="text-xs text-muted-foreground">Credit available</p>
          </div>
        </div>
        <div className="bg-card rounded-xl p-4 flex items-center gap-3">
          <Users className="h-5 w-5 text-secondary shrink-0" />
          <div>
            <p className="font-mono text-xl font-bold text-primary">
              {info.friends_signed_up}
            </p>
            <p className="text-xs text-muted-foreground">Friends joined</p>
          </div>
        </div>
        <div className="bg-card rounded-xl p-4 flex items-center gap-3">
          <Gift className="h-5 w-5 text-primary shrink-0" />
          <div>
            <p className="font-mono text-xl font-bold text-primary">
              {info.friends_rewarded}
            </p>
            <p className="text-xs text-muted-foreground">Rewards earned</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch">
        <button
          onClick={copyCode}
          className="flex-1 group bg-card border border-primary/20 rounded-xl px-4 py-3 text-left hover:border-primary/40 transition-colors"
          data-testid="copy-referral-code-btn"
        >
          <p className="text-xs text-muted-foreground mb-1">Your referral code</p>
          <p className="flex items-center justify-between gap-2">
            <span className="font-mono text-lg font-bold text-primary tracking-wider">
              {info.referral_code}
            </span>
            <Copy className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
          </p>
        </button>
        <div className="flex gap-2">
          <Button
            onClick={copyLink}
            variant="outline"
            className="gap-2"
            data-testid="copy-referral-link-btn"
          >
            <Copy className="h-4 w-4" />
            Copy link
          </Button>
          <Button
            onClick={nativeShare}
            className="btn-animated bg-primary hover:bg-primary/90 gap-2"
            data-testid="share-referral-btn"
          >
            <Share2 className="h-4 w-4" />
            Share
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReferralCard;
